import fetch from 'globals/wa-fetch';

import { Formik, Field, Form } from 'formik';
import { getStore } from 'app/hermes-redux';
import HelpCenterComp from 'react-components/help-center-comp/help-center-comp';
import TextInputField from 'wa-storybook/components/inputs/simple-input-field/text-input-comp';
import Button from 'wa-storybook/components/general/button/button';
import styles from './resend-invitation-form.module.scss';
import { goTo } from 'router/navigation';
import { lookupKeys, performTenancyLookup } from 'globals/regional-api';
import getFormSchema from './form-schema';
import ChevronIcon from 'wa-storybook/components/general/icon/components/chevron-back';
import { showToast } from 'react-components/toaster-comp/state/actions';
import { ToastType } from 'react-components/toaster-comp/state/types';
import { Link } from 'router/custom-link';
import { getZendeskLink } from 'state/global/actions';

const ResendInvitationForm = () => {
  const formSchema = getFormSchema();

  const submitEmailAddress = async data => {
    const { dispatch } = getStore();

    try {
      await performTenancyLookup(lookupKeys.email, data.email_address);

      await fetch('/user/reset-password-request', {
        method: 'post',
        version: 1.3,
        body: { email: data.email_address },
      });

      await goTo('resendInvitationSuccess');
    } catch (res) {
      dispatch(
        showToast({
          type: ToastType.ERROR,
          message: polyglot.t('sign_up.error'),
        }),
      );
    }
  };

  type FormikInputProps = {
    field: {
      name: string;
    };
    form: {
      setFieldValue: (arg1: string, arg2: string, arg3: boolean) => void;
      setFieldError: (arg1: string, arg2?: string | null | undefined) => void;
      errors: {
        [key: string]: string;
      };
    };
  };

  const FormikInput = ({ field, form }: FormikInputProps) => (
    <TextInputField
      id={field.name}
      label={polyglot.t('sign_up.invitation.email_field_label')}
      onChange={value => {
        form.setFieldValue(field.name, value, false);

        form.setFieldError(field.name, undefined);
      }}
      type='text'
      parentErrorMessage={form.errors[field.name]}
    />
  );

  return (
    <Formik
      initialValues={{ email_address: '' }}
      onSubmit={submitEmailAddress}
      validationSchema={formSchema}
    >
      {({ isSubmitting, dirty, isValid }) => (
        <Form className={styles.container}>
          <div className={styles.content}>
            <Link className={styles.back} to={'/signup/invitation'}>
              <ChevronIcon />
              <span className={styles['back-text']}>
                {polyglot.t('sign_up.invitation.back_button')}
              </span>
            </Link>
            <h1 className={styles.title}>
              {polyglot.t('sign_up.invitation.resend_title')}
            </h1>
            <div className={styles.details}>
              {polyglot.t('sign_up.invitation.you_can_find')}
            </div>
            <div className={styles.details}>
              {polyglot.t('sign_up.invitation.to_resend_telus')}
            </div>
            <div>
              <div className={styles['input-field']}>
                <Field name='email_address' component={FormikInput} />
              </div>
            </div>
            <div className={styles.button}>
              <Button
                dataHook='resend-invite'
                text={polyglot.t('sign_up.invitation.resend_button')}
                isDisabled={!dirty || !isValid}
                isLoading={isSubmitting}
              />
            </div>
          </div>
          <div className={styles.info}>
            <HelpCenterComp
              helpText={polyglot.t(
                'sign_up.invitation.help_and_accessibility_text_link',
                {
                  urlGettingStarted: getZendeskLink({ key: 'getting_started' }),
                  urlAccessibilityTips: getZendeskLink({
                    key: 'accessibility_tips',
                  }),
                },
              )}
            />
          </div>
        </Form>
      )}
    </Formik>
  );
};

ResendInvitationForm.displayName = 'ResendInvitationForm';

export default ResendInvitationForm;
